'use client';

export interface User {
  id: string;
  name: string;
  email: string;
  careerHubEnrolled: boolean;
  createdAt: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
}

// Dummy pre-seeded accounts
const DUMMY_ACCOUNTS: Array<User & { password: string }> = [
  {
    id: 'user-001',
    name: 'Marcus D.',
    email: 'marcus@demo.com',
    password: 'demo123',
    careerHubEnrolled: true,
    createdAt: '2026-03-12',
  },
  {
    id: 'user-002',
    name: 'Sarah K.',
    email: 'sarah@demo.com',
    password: 'demo123',
    careerHubEnrolled: false,
    createdAt: '2026-04-01',
  },
];

const SESSION_KEY = 'fca_session';
const USERS_KEY = 'fca_users';

function getStoredUsers(): Array<User & { password: string }> {
  if (typeof window === 'undefined') return DUMMY_ACCOUNTS;
  try {
    const stored = localStorage.getItem(USERS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return [...DUMMY_ACCOUNTS, ...parsed];
    }
  } catch {}
  return DUMMY_ACCOUNTS;
}

function saveNewUser(user: User & { password: string }) {
  if (typeof window === 'undefined') return;
  try {
    const stored = localStorage.getItem(USERS_KEY);
    const existing = stored ? JSON.parse(stored) : [];
    existing.push(user);
    localStorage.setItem(USERS_KEY, JSON.stringify(existing));
  } catch {}
}

export function login(email: string, password: string): { success: boolean; user?: User; error?: string } {
  const users = getStoredUsers();
  const found = users.find(
    (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
  );
  if (!found) {
    return { success: false, error: 'Invalid email or password.' };
  }
  const { password: _pw, ...user } = found;
  if (typeof window !== 'undefined') {
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  }
  return { success: true, user };
}

export function signUp(
  name: string,
  email: string,
  password: string
): { success: boolean; user?: User; error?: string } {
  const users = getStoredUsers();
  const exists = users.find((u) => u.email.toLowerCase() === email.toLowerCase());
  if (exists) {
    return { success: false, error: 'An account with this email already exists.' };
  }
  const newUser: User & { password: string } = {
    id: `user-${Date.now()}`,
    name,
    email,
    password,
    careerHubEnrolled: false,
    createdAt: new Date().toISOString().split('T')[0],
  };
  saveNewUser(newUser);
  const { password: _pw, ...user } = newUser;
  if (typeof window !== 'undefined') {
    localStorage.setItem(SESSION_KEY, JSON.stringify(user));
  }
  return { success: true, user };
}

export function logout() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(SESSION_KEY);
  }
}

export function getSession(): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) return JSON.parse(stored) as User;
  } catch {}
  return null;
}

export function enrollCareerHub(userId: string): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const session = getSession();
    if (!session || session.id !== userId) return null;
    const updated: User = { ...session, careerHubEnrolled: true };
    localStorage.setItem(SESSION_KEY, JSON.stringify(updated));

    // Also update in stored users list
    const stored = localStorage.getItem(USERS_KEY);
    if (stored) {
      const users = JSON.parse(stored);
      const idx = users.findIndex((u: User) => u.id === userId);
      if (idx !== -1) {
        users[idx].careerHubEnrolled = true;
        localStorage.setItem(USERS_KEY, JSON.stringify(users));
      }
    }
    return updated;
  } catch {}
  return null;
}
